import { Fragment, useEffect, useState } from 'react';

import { Duration, Frequency, MedDose, MedDoseInfo } from '../../../../store/types/prescription';

export const getDoseStr = (dose?: MedDose): string | undefined => {
    if (dose?.id && dose.value && dose.unit) {
        return `${dose.value} ${dose.unit}`;
    }

    if (dose?.custom) {
        return dose.custom;
    }

    return;
};

type InputProps = {
    className: string;
    placeholder: string;
    list?: string;
    onInputChange: (value: string) => void;
    autocomplete?: string;
    value: string;
    showDropdown?: boolean;
    onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
};

export const DInput: React.FC<InputProps> = ({
    className,
    placeholder,
    list,
    onInputChange,
    autocomplete = 'off',
    value,
    showDropdown = true,
    onBlur,
}: InputProps) => {
    const [isSoftCleared, setIsSoftCleared] = useState(false);
    const [inputPlaceholder, setInputPlaceholder] = useState(placeholder);
    const [inputValue, setInputValue] = useState(value);

    useEffect(() => {
        setInputValue(value);
        setInputPlaceholder(placeholder);
    }, [value]);

    const onBBlur = (e: React.FocusEvent<HTMLInputElement>): void => {
        if (!isSoftCleared) {
            onBlur && onBlur(e);
            return;
        }

        const target = e.target as HTMLInputElement;
        e.currentTarget.value = target.placeholder;
        setInputValue(target.placeholder);
        setInputPlaceholder(placeholder);

        setIsSoftCleared(false);

        onBlur && onBlur(e);
    };

    const onFocus = (e: React.FocusEvent<HTMLInputElement>): void => {
        const target = e.target as HTMLInputElement;
        if (!target.value) {
            return;
        }

        setInputPlaceholder(target.value);
        setInputValue('');

        setIsSoftCleared(true);
    };

    const onClear = (): void => {
        onInputChange('');
        setInputValue('');
        setIsSoftCleared(false);
        setInputPlaceholder(placeholder);
    };

    const onChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        const target = e.target as HTMLInputElement;
        onInputChange(target.value);
        setInputValue(target.value);
        setIsSoftCleared(false);
    };

    return (
        // <div className="relative">
        <Fragment>
            <input
                list={list}
                placeholder={inputPlaceholder}
                className={`${className} medicine-input`}
                onFocus={onFocus}
                onBlur={onBBlur}
                autoComplete={autocomplete === 'off' ? 'new-password' : autocomplete}
                value={inputValue}
                onChange={onChange}
            />
            {showDropdown && (
                <svg
                    width="16"
                    height="16"
                    viewBox="0 0 28 28"
                    className="cursor-pointer downarrow fill-neutral-5"
                    preserveAspectRatio="xMidYMid meet"
                >
                    <path d="M7.41,8.58L12,13.17L16.59,8.58L18,10L12,16L6,10L7.41,8.58Z"></path>
                </svg>
            )}
            <div className="container-fill">
                <svg
                    onClick={onClear}
                    width="16"
                    height="16"
                    viewBox="0 0 28 28"
                    className="cursor-pointer close fill-neutral-5"
                    preserveAspectRatio="xMidYMid meet"
                >
                    <path d="M12,2C17.53,2 22,6.47 22,12C22,17.53 17.53,22 12,22C6.47,22 2,17.53 2,12C2,6.47 6.47,2 12,2M15.59,7L12,10.59L8.41,7L7,8.41L10.59,12L7,15.59L8.41,17L12,13.41L15.59,17L17,15.59L13.41,12L17,8.41L15.59,7Z"></path>
                </svg>
            </div>
        </Fragment>
    );
};

export const IS_FIXED_FORM: string[] = ['tablet', 'tablets', 'capsule', 'capsules'];

export const getPrintableFrequency = (frequency?: Frequency): string => {
    return frequency?.custom || '';
};

export const getPrintableDuration = (duration?: Duration): string => {
    return duration?.value ? `${duration.value} ${duration.unit}` : duration?.custom || '';
};

export const getPrintableProperties = (m: MedDoseInfo): { what: string; value?: string }[] => {
    const doseStr = getDoseStr(m.dose);

    return [
        { what: 'start_from', value: m.start_from?.text },
        { what: 'to_day', value: m.to_day?.text },
        { what: 'dose', value: doseStr },
        { what: 'frequency', value: getPrintableFrequency(m.frequency) },
        { what: 'timing', value: m.timing },
        { what: 'duration', value: getPrintableDuration(m.duration) },
        { what: 'site_of_application', value: m.area?.name },
        { what: 'route', value: m.route?.name },
    ].filter((item) => item.value);
};
